import { DatePicker, Input, Select, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './PDTable'
import { ActivityIndicator, Text, View } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import {
  borderedDatePickerContainerStyle,
  borderedInputStyle,
  borderedSelectStyle
} from '../styles/policyDispatchingStyles'
import {
  formatDate,
  getStatusLabel,
  getStatusStyle,
  getStatusValue,
  parseDateString
} from '../utils/policyDispatchingUtils'
import PolicyDispatchingDialog from './PolicyDispatchingDialog'
import PDChangeInfoDialog from './PDChangeInfoDialog'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import {
  PolicyDispatchingService,
  StockOutPostOfficeCancelPayload,
  StockOutPostOfficeChangeDatePayload,
  StockOutPostOfficeContract,
  StockOutPostOfficeDeliveryPayload
} from '../services/PolicyDispatchingService'

const unwrapZioEither = (response: unknown): unknown =>
  response && typeof response === 'object' && '_tag' in response
    ? (response as any)._tag === 'Right'
      ? (response as any).right
      : null
    : response

const headCell = [
  { id: 1, label: 'Số hợp đồng' },
  { id: 2, label: 'Ngày phát hành đầu tiên' },
  { id: 3, label: 'Ngày phát hành' },
  { id: 4, label: 'Văn phòng' },
  { id: 5, label: 'Mã số đại lý' },
  { id: 6, label: 'Tên đại lý' },
  { id: 7, label: 'Tình trạng' },
  { id: 8, label: 'Mã hóa đơn' },
  { id: 9, label: 'Ngày bưu điện xác nhận' },
  { id: 10, label: 'Ngày khách hàng ký nhận' },
  { id: 11, label: 'Ngày giao hợp đồng' },
  { id: 12, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'contractNumber' },
  { id: 2, label: 'firstIssueDate' },
  { id: 3, label: 'issueDate' },
  { id: 4, label: 'branch' },
  { id: 5, label: 'agentNumber' },
  { id: 6, label: 'agentName' },
  { id: 7, label: 'status' },
  { id: 8, label: 'invoiceCode' },
  { id: 9, label: 'postOfficeConfirmDate' },
  { id: 10, label: 'customerSignDate' },
  { id: 11, label: 'deliveryDate' },
  { id: 12, label: 'remark' }
]

const statusOptions = [
  { value: 'AVAILABLE', label: 'Còn trong kho' },
  { value: 'IN_USED', label: 'Đã giao' },
  { value: 'RETURNED', label: 'Đã trả lại' }
]

type FormValues = {
  contractNumber: string
  agentNumber: string
  agentName: string
  latestIssueDate: string
  stockInDate: string
  invoiceCode: string
  remark: string
  deliveryDate: Date | null
  policyStatus: any
}

export type PDStockOutPostOfficeHandle = {
  handleDeliveryContracts: () => void
  handleCancelContracts: () => void
  handleAdjustDeliveryDate: () => void
}

function PDStockOutPostOffice(
  { styles }: { styles: { [key: string]: any } },
  ref: React.Ref<PDStockOutPostOfficeHandle>
) {
  const { officeCode } = usePolicyDispatchingOffice()
  const { control, handleSubmit, setValue, watch, getValues } = useForm<FormValues>({
    defaultValues: {
      contractNumber: '',
      agentNumber: '',
      agentName: '',
      latestIssueDate: '',
      stockInDate: '',
      invoiceCode: '',
      remark: '',
      deliveryDate: null,
      policyStatus: statusOptions[0]
    }
  })
  const [rows, setRows] = React.useState<StockOutPostOfficeContract[]>([])
  const [selectedRows, setSelectedRows] = React.useState<StockOutPostOfficeContract[]>([])
  const [allRowsSelected, setAllRowsSelected] = React.useState(false)
  const [pageInfo, setPageInfo] = React.useState<{
    page: number
    totalElements: number
    totalPages: number
    first: boolean
    last: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const [openChangeDeliveryDateDialog, setOpenChangeDeliveryDateDialog] = React.useState(false)
  const lastSearchValues = React.useRef<FormValues | null>(null)

  const search = (values: FormValues, page: number, resetSelection = false) => {
    if (!officeCode) {
      setModalInfo({ title: 'Văn phòng', message: 'Vui lòng chọn văn phòng trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    if (!values.contractNumber) {
      setModalInfo({ title: 'Số hợp đồng', message: 'Vui lòng nhập số hợp đồng trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchStockOutPostOffice({
        contractNumber: values.contractNumber,
        officeCode,
        status: values.policyStatus.value,
        page
      }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          const information = response.information
          if (information) {
            setValue('agentNumber', information.agentNumber)
            setValue('agentName', information.agentName)
            setValue('latestIssueDate', information.latestIssueDate || '')
            setValue('stockInDate', information.stockInDate || '')
            const preserveAllSelected = !resetSelection && allRowsSelected
            const selectedKeys = new Set((resetSelection ? [] : selectedRows).map((row) => row.contractNumber))
            if (resetSelection) {
              setSelectedRows([])
              setAllRowsSelected(false)
            }
            setRows(
              response.contracts.map((row) => ({
                ...row,
                status: getStatusLabel(row.status),
                checked: preserveAllSelected || selectedKeys.has(row.contractNumber)
              }))
            )
            setPageInfo({
              page: response.page,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              first: response.page === 0,
              last: response.page >= response.totalPages - 1
            })
          } else {
            setValue('agentNumber', '')
            setValue('agentName', '')
            setValue('latestIssueDate', '')
            setValue('stockInDate', '')
            setRows([])
            setSelectedRows([])
            setAllRowsSelected(false)
            setPageInfo(null)
            setModalInfo({ title: 'Tìm kiếm', message: 'Không tìm thấy thông tin được yêu cầu!' })
            setOpenModal(true)
          }
          setIsLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setRows([])
          setIsLoading(false)
          setModalInfo({ title: 'Tìm kiếm thất bại', message: 'Đã có lỗi xảy ra. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => {
    lastSearchValues.current = values
    search(values, 0, true)
  })

  const handlePageChange = (page: number) => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, page)
    }
  }

  const refreshSearch = () => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, 0, true)
    }
  }

  const handleDeliveryContracts = () => {
    if (selectedRows.length === 0) {
      setModalInfo({ title: 'Giao hợp đồng', message: 'Vui lòng chọn ít nhất một hợp đồng để giao.' })
      setOpenModal(true)
      return
    }

    const condition = selectedRows.every((item) => getStatusValue(item.status) === 'AVAILABLE')
    if (!condition) {
      setModalInfo({
        title: 'Giao hợp đồng',
        message: 'Chỉ có thể giao các hợp đồng đang ở trạng thái "Còn trong kho". Vui lòng kiểm tra lại.'
      })
      setOpenModal(true)
      return
    }

    const selectedDeliveryDate = watch('deliveryDate')
    const deliveryDate = formatDate(selectedDeliveryDate)
    if (!deliveryDate || !selectedDeliveryDate) {
      setModalInfo({ title: 'Giao hợp đồng', message: 'Vui lòng nhập ngày giao hợp đồng trước khi giao.' })
      setOpenModal(true)
      return
    }

    const stockInDate = parseDateString(watch('stockInDate'))
    if (!stockInDate) {
      setModalInfo({ title: 'Giao hợp đồng', message: 'Không xác định được ngày nhập kho để kiểm tra điều kiện giao.' })
      setOpenModal(true)
      return
    }

    const earliestDeliveryDate = new Date(stockInDate.getFullYear(), stockInDate.getMonth(), stockInDate.getDate() + 6)
    const normalizedDeliveryDate = new Date(
      selectedDeliveryDate.getFullYear(),
      selectedDeliveryDate.getMonth(),
      selectedDeliveryDate.getDate()
    )
    if (normalizedDeliveryDate < earliestDeliveryDate) {
      setModalInfo({
        title: 'Giao hợp đồng',
        message: 'Ngày giao hợp đồng phải từ ngày nhập kho cộng 6 ngày trở đi.'
      })
      setOpenModal(true)
      return
    }

    const payload: StockOutPostOfficeDeliveryPayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      invoiceCode: watch('invoiceCode'),
      deliveryDate: deliveryDate || '',
      remark: watch('remark')
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.deliveryStockOutPostOffice(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setAllRowsSelected(false)
          setSelectedRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Giao hợp đồng',
            message: 'Giao hợp đồng thành công.'
          })
          setOpenModal(true)
          refreshSearch()
          setValue('invoiceCode', '')
          setValue('deliveryDate', null)
          setValue('remark', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Giao hợp đồng',
            message: 'Không thể giao hợp đồng. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleCancelContracts = () => {
    if (selectedRows.length === 0) {
      setModalInfo({ title: 'Hủy giao hợp đồng', message: 'Vui lòng chọn ít nhất một hợp đồng để hủy giao.' })
      setOpenModal(true)
      return
    }

    const condition = selectedRows.every((item) => getStatusValue(item.status) === 'IN_USED')
    if (!condition) {
      setModalInfo({
        title: 'Hủy giao hợp đồng',
        message: 'Chỉ có thể hủy giao các hợp đồng đang ở trạng thái "Đã giao". Vui lòng kiểm tra lại.'
      })
      setOpenModal(true)
      return
    }
    const payload: StockOutPostOfficeCancelPayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      remark: watch('remark')
    }))
    setIsLoading(true)
    pipe(
      PolicyDispatchingService.cancelStockOutPostOffice(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setAllRowsSelected(false)
          setSelectedRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Hủy giao hợp đồng',
            message: 'Hủy giao hợp đồng thành công.'
          })
          setOpenModal(true)
          refreshSearch()
          setValue('invoiceCode', '')
          setValue('deliveryDate', null)
          setValue('remark', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Hủy giao hợp đồng',
            message: 'Không thể hủy giao hợp đồng. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleAdjustDeliveryDate = () => {
    if (selectedRows.length === 0) {
      setModalInfo({
        title: 'Điều chỉnh ngày giao',
        message: 'Vui lòng chọn ít nhất một hợp đồng để điều chỉnh ngày giao.'
      })
      setOpenModal(true)
      return
    }

    const condition = selectedRows.every((row) => getStatusValue(row.status) === 'IN_USED')
    if (!condition) {
      setModalInfo({
        title: 'Điều chỉnh ngày giao',
        message: 'Chỉ có thể điều chỉnh ngày giao cho các hợp đồng đang ở trạng thái "Đã giao". Vui lòng kiểm tra lại.'
      })
      setOpenModal(true)
      return
    }

    setOpenChangeDeliveryDateDialog(true)
  }

  const handleChangeDeliveryDate = (newDeliveryDate: Date, remark: string) => {
    const payload: StockOutPostOfficeChangeDatePayload[] = selectedRows.map((row) => ({
      contractNumber: row.contractNumber,
      newDeliveryDate: formatDate(newDeliveryDate) || '',
      remark: remark
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.changeDateStockOutPostOffice(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setAllRowsSelected(false)
          setSelectedRows([])
          setOpenChangeDeliveryDateDialog(false)
          setIsLoading(false)
          setModalInfo({
            title: 'Điều chỉnh ngày giao',
            message: 'Điều chỉnh ngày giao thành công.'
          })
          setOpenModal(true)
          refreshSearch()
          setValue('invoiceCode', '')
          setValue('deliveryDate', null)
          setValue('remark', '')
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Điều chỉnh ngày giao',
            message: 'Không thể điều chỉnh ngày giao. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  React.useImperativeHandle(ref, () => ({ handleDeliveryContracts, handleCancelContracts, handleAdjustDeliveryDate }))

  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 24 } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      <PDChangeInfoDialog
        visible={openChangeDeliveryDateDialog}
        selectedCount={selectedRows.length}
        currentDates={selectedRows.map((row) => row.deliveryDate)}
        stockInDates={[watch('stockInDate')]}
        minimumDaysAfterStockInDate={6}
        action="changeDate"
        title="Điều chỉnh ngày giao"
        dateLabel="Ngày giao mới"
        duplicateDateMessage="Ngày giao mới không được trùng với ngày giao hiện tại."
        stockInDateMessage="Ngày giao phải từ ngày nhập kho cộng 6 ngày trở đi."
        onClose={() => setOpenChangeDeliveryDateDialog(false)}
        onConfirm={(value, remark) => handleChangeDeliveryDate(value as Date, remark)}
      />
      <View
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 24, ...styles.column_1_4 } as any}
      >
        <Controller
          name="contractNumber"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              title={'Số hợp đồng'}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              errorMessage={error?.message}
              containerStyle={{ flex: 1 }}
              inputStyle={borderedInputStyle}
              filled
              required
              maxLength={8}
            />
          )}
        />
        <button style={styles.submitButton} type="button" onClick={handleSearch} disabled={isLoading}>
          <span style={styles.mr_5}>
            <assets.SearchWhite />
          </span>
          Tìm
        </button>
      </View>

      <Text style={{ fontWeight: 'bold', fontSize: '20px' } as any}>Kết quả tìm kiếm</Text>

      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="agentNumber"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Mã đại lý'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="agentName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên đại lý'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4} />
        <View style={styles.column_1_4} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="latestIssueDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ngày phát hành'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="stockInDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ngày nhập kho'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4} />
        <View style={styles.column_1_4} />
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <Text style={styles.subTitle}> - Thông tin bưu điện</Text>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              marginTop: 4,
              flex: 1
            } as any
          }
        />
      </View>

      <View style={styles.column_1_4}>
        <Controller
          name="invoiceCode"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              title={'Mã hóa đơn'}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              errorMessage={error?.message}
              inputStyle={borderedInputStyle}
              filled
            />
          )}
        />
      </View>

      <View style={styles.row}>
        <View style={{ width: '50%' } as any}>
          <Controller
            name="remark"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ghi chú'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyle={{ flex: 1 }}
                filled
                inputStyle={borderedInputStyle}
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={{ width: '15%' } as any}>
          <Controller
            name="deliveryDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={'Ngày giao hợp đồng'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
                inputStyle={{ paddingBottom: 3 }}
                required
              />
            )}
          />
        </View>
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <Text style={styles.subTitle}> - Danh sách hợp đồng</Text>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              flex: 1,
              marginTop: 4
            } as any
          }
        />
      </View>

      <View style={{ width: '15%' } as any}>
        <Controller
          name="policyStatus"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Select
              label={'Tình trạng'}
              options={statusOptions}
              placeholder={'Chọn tình trạng'}
              menuPlacement="below"
              onChange={(e) => {
                onChange(e)
                const searchValues = { ...getValues(), policyStatus: e }
                lastSearchValues.current = searchValues
                search(searchValues, 0, true)
              }}
              onBlur={onBlur}
              value={value}
              key={value?.value}
              errorMessage={error?.message}
              containerStyles={borderedSelectStyle}
              filled
            />
          )}
        />
      </View>

      <View style={{ marginTop: 30 } as any}>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          hasCheckbox
          hasCheckAll
          getStyle={(cell, value) => ({
            ...(cell === 'status' ? getStatusStyle(value) : {}),
            whiteSpace: 'nowrap'
          })}
          onSelectionChange={(nextRows, nextSelectedRows) => {
            setRows(nextRows)
            setSelectedRows((currentSelectedRows) => {
              const currentPageKeys = new Set(rows.map((row) => row.contractNumber))
              const selectionsFromOtherPages = currentSelectedRows.filter(
                (row) => !currentPageKeys.has(row.contractNumber)
              )
              const mergedSelectedRows = [...selectionsFromOtherPages, ...nextSelectedRows]

              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && mergedSelectedRows.length === pageInfo.totalElements)
              )

              return mergedSelectedRows
            })
          }}
          onSelectAllChange={(selected) => {
            setRows((currentRows) => currentRows.map((row) => ({ ...row, checked: selected })))

            if (!selected) {
              setSelectedRows([])
              setAllRowsSelected(false)
              return
            }

            if (!officeCode || !lastSearchValues.current || !pageInfo || pageInfo.totalPages <= 1) {
              setSelectedRows(rows)
              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && rows.length === pageInfo.totalElements)
              )
              return
            }

            const values = lastSearchValues.current
            setIsLoading(true)
            Promise.all(
              Array.from({ length: pageInfo.totalPages }, (_, page) =>
                pipe(
                  PolicyDispatchingService.searchStockOutPostOffice({
                    contractNumber: values.contractNumber,
                    officeCode,
                    status: values.policyStatus.value,
                    page
                  }),
                  ZIO.runPromise({})
                )
              )
            )
              .then((responses) => {
                const allContracts = responses.flatMap((response) => {
                  const data = unwrapZioEither(response) as any
                  return (
                    data?.contracts?.map((row: StockOutPostOfficeContract) => ({
                      ...row,
                      status: getStatusLabel(row.status),
                      checked: true
                    })) || []
                  )
                })
                setSelectedRows(allContracts.length > 0 ? allContracts : rows)
                setAllRowsSelected(true)
                setIsLoading(false)
              })
              .catch(() => {
                setSelectedRows(rows)
                setIsLoading(false)
                setModalInfo({
                  title: 'Chọn tất cả',
                  message: 'Không thể tải toàn bộ danh sách hợp đồng. Đã chọn các hợp đồng ở trang hiện tại.'
                })
                setOpenModal(true)
              })
          }}
          allRowsSelected={Boolean(allRowsSelected && pageInfo && pageInfo.totalElements > 0)}
          pagination={pageInfo ? { ...pageInfo, onPageChange: handlePageChange } : undefined}
        />
      </View>
    </View>
  )
}

export default React.forwardRef(PDStockOutPostOffice)
